import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Search, Filter, MapPin, DollarSign, Clock, Eye, AlertCircle,
  Loader2, Briefcase, ArrowRight, Lock, Crown, Users, Zap
} from "lucide-react";

// Helper function for budget display
const formatBudgetRange = (budgetMin, budgetMax) => {
  if (budgetMin && budgetMax) return `$${budgetMin} - $${budgetMax}`;
  if (budgetMax) return `Up to $${budgetMax}`;
  return `From $${budgetMin}`;
};

export default function BrowseLeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [accessLevel, setAccessLevel] = useState("limited");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    category: searchParams.get("category") || "",
    location: ""
  });

  const isProfessional = user?.user_type === "professional";
  const isSubscribed = user?.subscription_status === "active";
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [leadsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/leads`, {
            headers,
            params: {
              category: filters.category || undefined,
              location: filters.location || undefined
            }
          }),
          axios.get(`${API}/categories`)
        ]);
        
        setLeads(leadsRes.data.leads || []);
        setAccessLevel(leadsRes.data.access || "limited");
        setCategories(categoriesRes.data);
      } catch (error) {
        toast.error("Failed to load leads");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters.category, filters.location, token]);

  const filteredLeads = leads.filter(lead => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      // For limited access, search in preview fields
      if (accessLevel === "limited") {
        return (
          (lead.title_preview?.toLowerCase().includes(searchLower)) ||
          (lead.category?.toLowerCase().includes(searchLower))
        );
      }
      // For full access, search in all fields
      return (
        lead.title?.toLowerCase().includes(searchLower) ||
        lead.description?.toLowerCase().includes(searchLower) ||
        lead.category?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const getUrgencyBadge = (urgency) => {
    if (urgency === "urgent") {
      return <Badge className="bg-red-100 text-red-700">Urgent</Badge>;
    }
    return null;
  };

  const getCategoryName = (categoryId) => {
    return categories.find(c => c.id === categoryId)?.name || categoryId;
  };

  // Helper function for slots badge styling
  const getSlotsBadgeClass = (slotsRemaining) => {
    if (slotsRemaining === 0) return 'bg-red-100 text-red-700';
    if (slotsRemaining === 1) return 'bg-orange-100 text-orange-700';
    return 'bg-green-100 text-green-700';
  };

  // Helper function for slots badge text
  const getSlotsBadgeText = (lead) => {
    if (lead.slots_remaining === 0) return 'Fully Claimed';
    if (lead.already_viewed) return 'Already Viewed';
    return `${lead.slots_remaining} slot${lead.slots_remaining !== 1 ? 's' : ''} left`;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Check if user needs subscription
  const needsSubscription = accessLevel === "limited" && isProfessional;

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto" data-testid="browse-leads-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-2">
            Browse Available Leads
          </h1>
          <p className="text-gray-600">
            {filteredLeads.length} jobs available {filters.category && `in ${getCategoryName(filters.category)}`}
          </p>
        </div>

        {/* Subscription Required Banner for Professionals */}
        {needsSubscription && (
          <Card className="border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 mb-6" data-testid="subscription-required-banner">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                    <Lock className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-gray-900">Subscription Required</h3>
                    <p className="text-gray-600">
                      Subscribe to unlock full lead details, contact information, and start bidding.
                    </p>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button className="bg-blue-600 hover:bg-blue-700 rounded-full px-6" data-testid="subscribe-now-btn">
                    <Crown className="w-4 h-4 mr-2" />
                    Subscribe Now - Starting $25/month
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Non-logged in user banner */}
        {!user && (
          <Card className="border-gray-200 bg-gray-50 mb-6" data-testid="login-prompt-banner">
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-gray-500" />
                  <p className="text-gray-700 text-sm">
                    Sign in as a professional and subscribe to access full lead details.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="rounded-full">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 rounded-full">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-stone-200 mb-6">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search jobs..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 h-11"
                  data-testid="search-input"
                />
              </div>
              
              <Select
                value={filters.category}
                onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}
              >
                <SelectTrigger className="w-full md:w-48 h-11" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative w-full md:w-48">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Location"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  className="pl-10 h-11"
                  data-testid="location-filter"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leads List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-forest" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <Card className="border-stone-200 border-dashed" data-testid="no-jobs-found">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">No jobs found</h3>
              <p className="text-gray-500">Try adjusting your filters or check back later.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredLeads.map((lead) => (
              <Card
                key={lead.id}
                className={`border-stone-200 hover:shadow-md transition-shadow ${lead.is_locked ? 'bg-gray-50/50' : ''}`}
                data-testid={`lead-card-${lead.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className="bg-forest/10 text-forest">{getCategoryName(lead.category)}</Badge>
                        {lead.bidding_enabled && (
                          <Badge className="bg-blue-100 text-blue-700" data-testid={`bidding-badge-${lead.id}`}>
                            <Zap className="w-3 h-3 mr-1" />
                            Bidding
                          </Badge>
                        )}
                        {getUrgencyBadge(lead.urgency)}
                        <span className="text-xs text-gray-500">{formatTimeAgo(lead.created_at)}</span>
                        {lead.is_locked && (
                          <Badge className="bg-gray-100 text-gray-600 flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Locked
                          </Badge>
                        )}
                        {!lead.is_locked && lead.slots_remaining !== undefined && (
                          <Badge className={`flex items-center gap-1 ${getSlotsBadgeClass(lead.slots_remaining)}`}>
                            <Users className="w-3 h-3" />
                            {getSlotsBadgeText(lead)}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Title - Full or Preview */}
                      <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">
                        {lead.is_locked ? (
                          <span className="text-gray-600">{lead.title_preview}</span>
                        ) : (
                          lead.title
                        )}
                      </h3>
                      
                      {/* Description - Only for full access */}
                      {!lead.is_locked && lead.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {lead.description}
                        </p>
                      )}
                      
                      {/* Lead is locked - show teaser */}
                      {lead.is_locked && (
                        <p className="text-gray-400 text-sm mb-4 italic">
                          Subscribe to view full job details and customer contact...
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {lead.is_locked ? lead.location_preview : lead.location}
                        </span>
                        {/* Budget info */}
                        {lead.is_locked ? (
                          lead.has_budget && (
                            <span className="flex items-center gap-1 text-gray-400">
                              <DollarSign className="w-4 h-4" />
                              Budget available
                            </span>
                          )
                        ) : (
                          (lead.budget_min || lead.budget_max) && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {formatBudgetRange(lead.budget_min, lead.budget_max)}
                            </span>
                          )
                        )}
                        <span className="text-gray-400">•</span>
                        <span>{lead.responses_count} quotes</span>
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      {lead.is_locked ? (
                        <Link to="/pricing">
                          <Button className="bg-blue-600 hover:bg-blue-700 rounded-full" data-testid={`unlock-lead-${lead.id}`}>
                            <Lock className="w-4 h-4 mr-2" />
                            Unlock Lead
                          </Button>
                        </Link>
                      ) : (
                        <Link to={`/job/${lead.id}`}>
                          <Button variant="outline" className="rounded-full border-forest/20 hover:bg-forest/5" data-testid={`view-lead-${lead.id}`}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
